import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Radar, Sliders, Bookmark, Zap, ArrowRight, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import WelcomeRadar from "@/components/WelcomeRadar";
import Footer from "@/components/Footer";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function Landing() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const features = [
    { icon: Radar, title: t("feat.radarT"), desc: t("feat.radarD") },
    { icon: Sliders, title: t("feat.settingsT"), desc: t("feat.settingsD") },
    { icon: Zap, title: t("feat.matchT"), desc: t("feat.matchD") },
    { icon: Bookmark, title: t("feat.trackT"), desc: t("feat.trackD") },
  ];

  return (
    <div className="min-h-screen wo-bg">
      {/* ================= MOBILE WELCOME ================= */}
      <div className="lg:hidden min-h-screen flex flex-col relative overflow-hidden">
        <div className="absolute inset-0 wo-glow-radial pointer-events-none" />
        <div className="flex justify-end p-4 relative z-10">
          <LanguageSwitcher testId="language-switcher-mobile" />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
          <WelcomeRadar />
        </motion.div>

        <div className="relative z-10 px-6 pb-10 space-y-3">
          <motion.div initial={{ opacity: 0, y: 44 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}>
            <Link to={user ? "/radar" : "/register"} data-testid="welcome-get-started">
              <Button className="w-full liquid-glass border border-teal-400/40 text-teal-100 font-semibold hover:bg-teal-500/10 py-6 text-base">
                {t("nav.getStarted")}
              </Button>
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 44 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8, ease: "easeOut" }}>
            <Link to={user ? "/radar" : "/login"} data-testid="welcome-login">
              <Button variant="ghost" className="w-full text-slate-300 hover:text-white hover:bg-white/5 py-6 text-base">
                {user ? t("hero.ctaRadar") : t("auth.loginBtn")}
              </Button>
            </Link>
          </motion.div>
        </div>

        <div className="relative z-10 px-6 pb-8 flex items-center justify-center" data-testid="welcome-bottom-bar">
          <div className="liquid-glass rounded-lg px-2">
            <LanguageSwitcher testId="welcome-language-bottom" dropUp showName label={t("common.language")} />
          </div>
        </div>

        <div className="relative z-10 px-6 pb-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-slate-500" data-testid="welcome-legal-links">
          <Link to="/legal#disclaimer" className="hover:text-teal-300 transition-colors">About</Link>
          <span className="text-slate-700">·</span>
          <Link to="/legal#terms" className="hover:text-teal-300 transition-colors">Terms</Link>
          <span className="text-slate-700">·</span>
          <Link to="/legal#privacy" className="hover:text-teal-300 transition-colors">Privacy</Link>
          <span className="text-slate-700">·</span>
          <Link to="/legal#gdpr" className="hover:text-teal-300 transition-colors">GDPR</Link>
        </div>
      </div>

      {/* ================= WEB LANDING ================= */}
      <div className="hidden lg:block">
        <Navbar showLanguage />
        <section className="relative overflow-hidden min-h-[calc(100vh-4rem)] flex items-center justify-center">
          <div className="absolute inset-0 wo-glow-radial pointer-events-none" />
          <div className="relative w-full max-w-6xl mx-auto px-8 py-12 grid grid-cols-2 gap-6 items-center">
            <div className="text-left">
              <motion.span
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-cyan-400 border border-cyan-500/25 rounded-full px-4 py-1.5 mb-6">
                <MapPin size={12} /> {t("hero.badge")}
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
                className="font-display font-extrabold text-5xl lg:text-6xl tracking-tight text-slate-50 leading-[1.05]">
                {t("hero.title1")} <br />
                <span className="text-gradient">{t("hero.title2")}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-5 text-lg text-slate-400 max-w-lg">
                {t("hero.subtitle")}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-8 flex flex-wrap items-center gap-3">
                <Link to={user ? "/radar" : "/register"} data-testid="hero-radar-cta">
                  <Button className="bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90 px-7 py-6 text-base">
                    <Radar size={18} className="mr-2" /> {t("hero.ctaRadar")}
                  </Button>
                </Link>
                <Link to="/jobs" data-testid="hero-browse-cta">
                  <Button variant="outline" className="liquid-glass border-white/10 text-slate-200 hover:bg-white/5 px-7 py-6 text-base">
                    {t("hero.ctaBrowse")} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5 }}
                className="mt-10 flex items-center gap-8">
                {[["16+", t("hero.liveRoles")], ["50km", t("hero.radarRange")], ["6", t("hero.categories")]].map(([n, l]) => (
                  <div key={l} className="text-center">
                    <div className="font-display font-bold text-2xl text-teal-300">{n}</div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-mono">{l}</div>
                  </div>
                ))}
              </motion.div>
            </div>
            <div className="flex flex-col items-center">
              <WelcomeRadar />
              <div className="flex items-center gap-2 mt-8">
                {["#2DD4BF", "#06B6D4", "#3B82F6"].map((c, i) => (
                  <motion.span key={i} className="w-2 h-2 rounded-full" style={{ background: c }}
                    animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
                    transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-8 pb-28 pt-4">
          <div className="grid grid-cols-4 gap-5">
            {features.map((f, i) => (
              <motion.div key={f.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl liquid-glass p-6 hover:border-teal-500/30 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400 mb-4">
                  <f.icon size={20} />
                </div>
                <h3 className="font-display font-semibold text-slate-100 mb-1.5">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}
